import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { InvestmentPathStrip } from "./InvestmentPathStrip";
import { MimshakService } from "../../services/mimshakService";
import { PolicyOrAccount } from "../../interfaces/DTO/mislaka_xml_models";

import AccumulatedSavingsTable from "./Tabels/AccumulatedSavingsTable";
import SavingsSummary from "./SavingsSummary";
import { ManagementFeeDetailsTable } from "./Tabels/ManagementFeeDetailsTable";
import { formatPensionFeeExpenseType } from "../../utils/enumFormatter";
import {
  formatCurrency,
  formatIsoDateToMMYYYY,
  formatPercentage,
} from "../../utils/namesFormater";
import { fundService } from "../../services/fundService";

interface PolicyPathsProps {
  policies: PolicyOrAccount[];
}

export const PolicyPaths: React.FC<PolicyPathsProps> = ({ policies }) => {
  const [pathCodes, setPathCodes] = useState<number[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyOrAccount | null>(
    null
  );
 
  const fundData = fundService.getFundsFromCacheByIds(pathCodes);
 const theme = useTheme();
  // Extract path codes on mount or when policies change
  useEffect(() => {
    const codes = MimshakService.extractPathCodes(policies);
    setPathCodes(codes);
  }, [policies]);

  if (!policies || policies.length === 0) {
    return <Typography variant="h6">אין פוליסות להצגה</Typography>;
  }

  // If a policy is selected, show only the AccumulatedSavingsTable
  if (selectedPolicy) {
    return (
      <Box dir="rtl">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <IconButton
             onClick={() => setSelectedPolicy(null)}
               aria-label="clear selection"
            sx={{
              mb: 2,
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
              alignSelf: "flex-start", // ✅ מצמיד את הכפתור לשמאל (או לימין ב-RTL)
            }}
          >
            <ArrowBackIcon />
          </IconButton>
         
        </Box>
      
        <AccumulatedSavingsTable policy={selectedPolicy} />
        <ManagementFeeDetailsTable policy={selectedPolicy} />
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      {policies.map((policy, policyIndex) => {
        // Merge investment paths for the current policy
        const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(
          policy.investment_paths
        );

        return (
          <Box key={policyIndex} sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2, // רווחים בין האלמנטים
                }}
              >
                <Typography variant="h6">
                  פוליסה {policyIndex + 1} |{" "}
                  <span style={{ color: "gray" }}>{policy.policy_number}</span>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1, // רווחים בין הפריטים בתוך כל קבוצה
                }}
              >
                <Typography variant="body1">תשואה מתחילת השנה</Typography>
                <Typography variant="body1">
                  <span dir="ltr">{formatCurrency(policy.net_return_amount)}</span>
                </Typography>
                <Tooltip title="תאריך נכונות">
                  <Typography variant="body1">
                    | {formatIsoDateToMMYYYY(policy.update_date)}
                  </Typography>
                </Tooltip>
              </Box>
              <IconButton
                onClick={() => setSelectedPolicy(policy)}
                color="primary"
                aria-label="select policy"
              >
                <TextSnippetIcon />
                עוד פרטים

              </IconButton>
            </Box>
            {mergedPaths.map((path, pathIndex) => {
              const funds = fundData?.filter(
                (f) => f.fund_id === path.path_code
              );

              return (
                <Box key={pathIndex} sx={{ mt: 2 }}>
               
                    <InvestmentPathStrip
                      pathDetails={path} // שימוש באובייקט לאחר merge
                      fundData={funds || []} // העברת המערך לקומפוננטה
                      management_fee_details={policy.management_fee_details}
                      showDepositPercentage={mergedPaths.length > 1} 
                    />
                 
                </Box>
              );
            })}
            <SavingsSummary policy={policy} />
          </Box>
        );
      })}
    </Box>
  );
};
